import { ref, toRef, watch } from 'vue';
import { showNotify } from 'vant';
import { getWxAuthInfo } from '@/api/common';
import { getToken, setToken } from '@/utils/auth';
import { isTeachMode } from '@/utils/env';
import { getWechatAuthUrl } from '@/utils/wechatAuth';
import { isWxAuthUrl } from '@/utils/tool';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores';

/**
 * 认证初始化相关的composable函数
 * 处理应用启动时的认证逻辑，包括token检查、微信授权等
 */
export function useAuthInit() {
  const tokenReady = ref(false);
  const authStore = useAuthStore();
  const userStore = useUserStore();

  // 使用 toRef 来创建响应式引用，解决持久化插件导致的类型推断问题
  const codeRef = toRef(authStore, 'code');
  const $stopWatchCode = watch(
    () => codeRef.value,
    (newCode) => {
      if (newCode) {
        if (isTeachMode()) {
          tokenReady.value = true;
          return;
        } else {
          handleUserWxAuth();
        }
        $stopWatchCode();
      }
    },
  );

  /**
   * 检查是否已有有效token
   * @returns {boolean} 是否有token
   */
  const checkExistingToken = (): boolean => {
    const token = getToken();
    if (token) {
      console.log('存在token');
      tokenReady.value = true;
      return true;
    }
    return false;
  };

  /**
   * 处理用户端微信授权
   * @returns {Promise<void>}
   */
  const handleUserWxAuth = async (): Promise<void> => {
    // 统一使用 authStore 中的code 使用过一次后就会被清除
    const wxCode = authStore.getCode();
    try {
      const res = await getWxAuthInfo({ code: wxCode });

      // 设置token
      const accessToken = res.Result?.access_token;
      if (accessToken) {
        setToken(accessToken);
      }

      // 设置用户信息
      const userId = res.Result?.user_id;
      if (userId) {
        userStore.setUserId(userId);
      }

      tokenReady.value = true;
    } catch (error) {
      console.error('微信授权失败:', error);
      showNotify({
        message: '登录失败，请联系管理员',
        type: 'danger',
      });
    }
  };

  /**
   * 重定向到微信授权页面
   */
  const redirectToWxAuth = (): void => {
    location.href = getWechatAuthUrl();
  };

  /**
   * 初始化认证流程
   * 这是主要的入口函数，按顺序检查各种认证状态
   * @returns {Promise<void>}
   */
  const initAuth = async (): Promise<void> => {
    console.count('初始化认证流程');
    console.log('初始化认证流程', tokenReady.value);

    try {
      // 1. 首先检查是否已有token
      if (checkExistingToken()) {
        return;
      }

      // 2. 如果是微信授权回调，先设置tokenReady为false
      if (isWxAuthUrl()) {
        tokenReady.value = false;
      } else if (isTeachMode()) {
        tokenReady.value = true;
      } else {
        redirectToWxAuth();
      }
    } catch (error) {
      console.error('认证初始化失败:', error);
      showNotify({
        message: '系统初始化失败，请刷新重试',
        type: 'danger',
      });
    }
  };

  return {
    tokenReady,
    initAuth,
    checkExistingToken,
    handleUserWxAuth,
    redirectToWxAuth,
  };
}
